import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';

import type { DomainEvent } from '$domain/events';

interface ComebackDbSchema extends DBSchema {
  events: {
    key: string;
    value: DomainEvent;
    // A foreign event type is deliberately storable during sync/import.
    indexes: { type: string };
  };
  scratch: {
    key: string;
    value: unknown;
  };
}

let databasePromise: Promise<IDBPDatabase<ComebackDbSchema>> | undefined;

export function openComebackDb(): Promise<IDBPDatabase<ComebackDbSchema>> {
  if (databasePromise !== undefined) return databasePromise;

  const opening = openDB<ComebackDbSchema>('comeback', 1, {
    upgrade(database) {
      if (!database.objectStoreNames.contains('events')) {
        const events = database.createObjectStore('events', { keyPath: 'id' });
        events.createIndex('type', 'type');
      }
      if (!database.objectStoreNames.contains('scratch')) {
        database.createObjectStore('scratch');
      }
    },
    blocked() {
      // Another tab holds an older version open. Without this handler the open
      // simply never settles and every caller waits forever — which, on the
      // Catch screen, looks exactly like the app being broken.
      console.warn('[comeback] Database upgrade blocked by another open tab');
    },
    terminated() {
      // The browser killed the connection (storage pressure, a crashed
      // process). Drop the cache so the next call reopens rather than reusing a
      // dead handle.
      databasePromise = undefined;
    },
  }).then((database) => {
    database.onversionchange = () => {
      database.close();
      databasePromise = undefined;
    };
    database.onclose = () => {
      databasePromise = undefined;
    };
    return database;
  });

  // A rejected promise must not be cached: a single transient failure (a
  // storage hiccup, a blocked upgrade that later clears) would otherwise poison
  // every subsequent store operation for the life of the page, with no path
  // back except a reload.
  databasePromise = opening.catch((error: unknown) => {
    databasePromise = undefined;
    throw error;
  });

  return databasePromise;
}

/** Keeps database cleanup deterministic in Node tests without affecting app data paths. */
export async function closeComebackDbForTests(): Promise<void> {
  if (databasePromise === undefined) return;
  const database = await databasePromise;
  database.close();
  databasePromise = undefined;
}
